import { useState, useEffect } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { LoginScreen } from './components/LoginScreen';
import { StationListScreen } from './components/StationListScreen';
import { ChannelListScreen } from './components/ChannelListScreen';
import { ChannelWorkspaceScreen } from './components/ChannelWorkspaceScreen';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import {
  Station,
  Channel,
  Program,
} from './data/mockData';
import { toUtcIso } from './utils/dateTime';
import {
  useCreateChannelMutation,
  useDeleteChannelMutation,
  useCreateProgramMutation,
  useCreateRecurringProgramMutation,
  useCreateStationMutation,
  useDeleteStationMutation,
  useDeleteProgramMutation,
  useDeleteTrackMutation,
  useListChannelsQuery,
  useListStationsQuery,
  useUpdateChannelMutation,
  useUpdateProgramMutation,
  useUpdateStationMutation,
  useUploadTrackMutation,
} from '../store/api';
import { API_BASE_URL } from '../store/api';

type Screen = 'login' | 'stations' | 'channels' | 'workspace';
type UserRole = 'station' | 'superadmin';

export default function App() {
  const lastStateKey = 'radio.last_state';
  const capitalizeFirst = (value: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

  const getApiErrorMessage = (error: unknown, fallback: string) => {
    if (error && typeof error === 'object') {
      const maybeError = error as { data?: unknown; status?: number | string };
      if (maybeError.data && typeof maybeError.data === 'object') {
        const maybeData = maybeError.data as { detail?: unknown; message?: unknown };
        if (typeof maybeData.detail === 'string') {
          return maybeData.detail;
        }
        if (typeof maybeData.message === 'string') {
          return maybeData.message;
        }
      }
      if (typeof maybeError.data === 'string' && maybeError.data.trim().length > 0) {
        return maybeError.data;
      }
      if (maybeError.status) {
        return `${fallback} (status ${maybeError.status}).`;
      }
    }

    return fallback;
  };

  const [currentScreen, setCurrentScreen] = useState<Screen>('login');
  const [userRole, setUserRole] = useState<UserRole>('station');
  const [currentUsername, setCurrentUsername] = useState<string>('');
  const [loginStationId, setLoginStationId] = useState<number | null>(null);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [authRestoring, setAuthRestoring] = useState(true);
  const [pendingRestore, setPendingRestore] = useState<{
    screen: Screen;
    stationId?: string | null;
    channelId?: string | null;
  } | null>(null);

  const { data: stationsData = [] } = useListStationsQuery();
  const stationId = selectedStation ? Number(selectedStation.id) : undefined;
  const channelId = selectedChannel ? Number(selectedChannel.id) : undefined;

  const { data: channelsData = [] } = useListChannelsQuery(
    Number.isFinite(stationId) ? stationId! : skipToken
  );


  const [createStation] = useCreateStationMutation();
  const [updateStation] = useUpdateStationMutation();
  const [deleteStation] = useDeleteStationMutation();
  const [createChannel] = useCreateChannelMutation();
  const [updateChannel] = useUpdateChannelMutation();
  const [deleteChannel] = useDeleteChannelMutation();
  const [createProgram] = useCreateProgramMutation();
  const [createRecurringProgram] = useCreateRecurringProgramMutation();
  const [updateProgram] = useUpdateProgramMutation();
  const [deleteProgram] = useDeleteProgramMutation();
  const [uploadTrack] = useUploadTrackMutation();
  const [deleteTrack] = useDeleteTrackMutation();

  const stations = (stationsData || []).map((station: any) => ({
    ...station,
    id: String(station.id),
    loginUsername: station.loginUsername ?? station.login_username ?? station.loginUsername,
    loginPassword: station.loginPassword ?? station.login_password ?? station.loginPassword,
  })) as Station[];
  const channels = (channelsData || []).map((channel: any) => ({
    ...channel,
    id: String(channel.id),
    stationId: String(channel.stationId ?? channel.station_id),
    adminSuspend: Boolean(channel.adminSuspend ?? channel.admin_suspend),
    stationSuspend: Boolean(channel.stationSuspend ?? channel.station_suspend),
  })) as Channel[];


  // Disable dark mode for light theme
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  // Dynamic page title based on current screen
  useEffect(() => {
    const base = 'Radio Stream';
    switch (currentScreen) {
      case 'login':
        document.title = `Login | ${base}`;
        break;
      case 'stations':
        document.title = `Stations | ${base}`;
        break;
      case 'channels':
        document.title = selectedStation
          ? `${selectedStation.name} - Channels | ${base}`
          : `Channels | ${base}`;
        break;
      case 'workspace':
        document.title = selectedChannel
          ? `${selectedChannel.name} | ${base}`
          : `Workspace | ${base}`;
        break;
      default:
        document.title = base;
    }
  }, [currentScreen, selectedStation, selectedChannel]);

  const saveLastState = (screen: Screen, stationId?: string | number | null, channelId?: string | number | null) => {
    window.localStorage.setItem(
      lastStateKey,
      JSON.stringify({
        screen,
        stationId: stationId != null ? String(stationId) : null,
        channelId: channelId != null ? String(channelId) : null,
      })
    );
  };

  const handleLogin = (role: UserRole, stationId: number | null | undefined, username: string) => {
    setUserRole(role);
    setLoginStationId(stationId ?? null);
    setCurrentUsername(username);
    if (role === 'superadmin') {
      setCurrentScreen('stations');
      saveLastState('stations');
    } else {
      setCurrentScreen('channels');
      saveLastState('channels', stationId ?? null);
    }
  };

  useEffect(() => {
    const restoreSession = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          credentials: 'include',
        });
        if (response.status === 401) {
          const refreshed = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
          });
          if (!refreshed.ok) {
            setCurrentScreen('login');
            return;
          }
          const retry = await fetch(`${API_BASE_URL}/auth/me`, {
            credentials: 'include',
          });
          if (!retry.ok) {
            setCurrentScreen('login');
            return;
          }
          const data = (await retry.json()) as {
            role: UserRole;
            stationId?: number | null;
            username: string;
          };
          setUserRole(data.role);
          setLoginStationId(data.stationId ?? null);
          setCurrentUsername(data.username);
          setCurrentScreen(data.role === 'superadmin' ? 'stations' : 'channels');
          const rawLast = window.localStorage.getItem(lastStateKey);
          if (rawLast) {
            try {
              const parsed = JSON.parse(rawLast) as {
                screen?: Screen;
                stationId?: string | null;
                channelId?: string | null;
              };
              if (parsed.screen) {
                setPendingRestore({
                  screen: parsed.screen,
                  stationId: parsed.stationId ?? null,
                  channelId: parsed.channelId ?? null,
                });
              }
            } catch {
              // Ignore parse errors
            }
          }
          return;
        }

        if (!response.ok) {
          setCurrentScreen('login');
          return;
        }

        const data = (await response.json()) as {
          role: UserRole;
          stationId?: number | null;
          username: string;
        };
        setUserRole(data.role);
        setLoginStationId(data.stationId ?? null);
        setCurrentUsername(data.username);
        setCurrentScreen(data.role === 'superadmin' ? 'stations' : 'channels');
        const rawLast = window.localStorage.getItem(lastStateKey);
        if (rawLast) {
          try {
            const parsed = JSON.parse(rawLast) as {
              screen?: Screen;
              stationId?: string | null;
              channelId?: string | null;
            };
            if (parsed.screen) {
              setPendingRestore({
                screen: parsed.screen,
                stationId: parsed.stationId ?? null,
                channelId: parsed.channelId ?? null,
              });
            }
          } catch {
            // Ignore parse errors
          }
        }
      } catch {
        // Ignore restore errors
        setCurrentScreen('login');
      } finally {
        setAuthRestoring(false);
      }
    };

    restoreSession();
  }, []);

  useEffect(() => {
    if (!pendingRestore) {
      return;
    }

    const { screen, stationId, channelId } = pendingRestore;
    const stationMatch = stationId
      ? stations.find((station) => String(station.id) === String(stationId))
      : null;
    if (stationMatch && (!selectedStation || selectedStation.id !== stationMatch.id)) {
      setSelectedStation(stationMatch);
    }

    if (screen === 'workspace') {
      if (!stationMatch && !selectedStation) {
        return;
      }
      const channelMatch = channelId
        ? channels.find((channel) => String(channel.id) === String(channelId))
        : null;
      if (channelMatch) {
        setSelectedChannel(channelMatch);
        setCurrentScreen('workspace');
        setPendingRestore(null);
        return;
      }
    }

    if (screen === 'channels' && (stationMatch || selectedStation)) {
      setCurrentScreen('channels');
      setPendingRestore(null);
    }

    if (screen === 'stations' && userRole === 'superadmin') {
      setCurrentScreen('stations');
      setPendingRestore(null);
    }
  }, [pendingRestore, stations, channels, selectedStation, userRole]);

  useEffect(() => {
    if (userRole !== 'station' || currentScreen !== 'channels') {
      return;
    }

    if (loginStationId) {
      const matched = stations.find((station) => Number(station.id) === loginStationId);
      if (matched) {
        setSelectedStation(matched);
        return;
      }
    }

    if (!selectedStation && stations.length > 0) {
      setSelectedStation(stations[0]);
    }
  }, [userRole, currentScreen, selectedStation, stations, loginStationId]);

  const handleLogout = () => {
    fetch(`${API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {
      // Ignore logout errors
    });
    window.localStorage.removeItem(lastStateKey);
    setCurrentScreen('login');
    setUserRole('station');
    setLoginStationId(null);
    setCurrentUsername('');
    setSelectedStation(null);
    setSelectedChannel(null);
  };

  const handleSelectStation = (station: Station) => {
    setSelectedStation(station);
    setCurrentScreen('channels');
    saveLastState('channels', station.id);
  };

  const handleBackToStations = () => {
    setSelectedStation(null);
    setSelectedChannel(null);
    setCurrentScreen('stations');
    saveLastState('stations');
  };

  const handleSelectChannel = (channel: Channel) => {
    setSelectedChannel(channel);
    setCurrentScreen('workspace');
    saveLastState('workspace', selectedStation?.id ?? loginStationId, channel.id);
  };

  const handleBackToChannels = () => {
    setSelectedChannel(null);
    setCurrentScreen('channels');
    saveLastState('channels', selectedStation?.id ?? loginStationId);
  };

  const handleCreateChannel = async (channelData: Omit<Channel, 'id' | 'stationId'>) => {
    if (!selectedStation) {
      return;
    }

    const stationIdValue = Number(selectedStation.id);
    if (!Number.isFinite(stationIdValue)) {
      return;
    }

    const normalizedName = capitalizeFirst(channelData.name.trim());

    await createChannel({
      stationId: stationIdValue,
      channel_number: channelData.channelNumber,
      name: normalizedName,
      status: channelData.status,
    }).unwrap();
  };

  const handleUpdateChannel = async (
    channelIdValue: string | number,
    channelData: Omit<Channel, 'id' | 'stationId'>
  ) => {
    const numericChannelId = Number(channelIdValue);
    if (!Number.isFinite(numericChannelId)) {
      return;
    }

    const normalizedName = capitalizeFirst(channelData.name.trim());

    const updated = await updateChannel({
      channelId: numericChannelId,
      name: normalizedName,
      status: channelData.status,
    }).unwrap();

    if (selectedChannel?.id === channelIdValue) {
      setSelectedChannel({
        ...selectedChannel,
        name: updated.name,
        status: updated.status,
      });
    }
  };

  const handleDeleteChannel = async (channelIdValue: string | number) => {
    const numericChannelId = Number(channelIdValue);
    if (!Number.isFinite(numericChannelId)) {
      return;
    }

    await deleteChannel(numericChannelId).unwrap();

    if (selectedChannel?.id === channelIdValue) {
      setSelectedChannel(null);
      setCurrentScreen('channels');
    }
  };

  const handleUpdateStation = async (
    stationIdValue: string | number,
    stationData: Omit<Station, 'id' | 'channelCount'>
  ) => {
    const numericStationId = Number(stationIdValue);
    if (!Number.isFinite(numericStationId)) {
      return;
    }

    const updated = await updateStation({
      stationId: numericStationId,
      name: stationData.name,
      status: stationData.status,
    }).unwrap();

    if (selectedStation?.id === stationIdValue) {
      setSelectedStation({
        ...selectedStation,
        name: updated.name,
        status: updated.status,
        code: updated.code,
        channelCount: updated.channelCount,
      });
    }
  };

  const handleDeleteStation = async (stationIdValue: string | number) => {
    const numericStationId = Number(stationIdValue);
    if (!Number.isFinite(numericStationId)) {
      return;
    }

    await deleteStation(numericStationId).unwrap();

    if (selectedStation?.id === stationIdValue) {
      setSelectedStation(null);
      setSelectedChannel(null);
      setCurrentScreen('stations');
    }
  };

  const handleCreateStation = async (stationData: { name: string; status: 'Active' | 'Suspended' }) => {
    // Generate a station code from the name
    const code = stationData.name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 10);

    const created = await createStation({
      name: stationData.name,
      code,
      status: stationData.status,
    }).unwrap();

    if (created?.loginUsername && created?.loginPassword) {
      toast.success('Station credentials created', {
        description: `Username: ${created.loginUsername} | Password: ${created.loginPassword}`,
      });
    }
  };

  const handleSaveProgram = async (programData: Omit<Program, 'id' | 'channelId'>) => {
    if (!selectedChannel) {
      return;
    }

    const numericChannelId = Number(selectedChannel.id);
    if (!Number.isFinite(numericChannelId)) {
      return;
    }

    const startUtc = toUtcIso(new Date(programData.startDateTime));
    const endUtc = toUtcIso(new Date(programData.endDateTime));

    const payload: Record<string, unknown> = {
      title: programData.title,
      trackId: programData.trackId ? Number(programData.trackId) : null,
      startDateTime: startUtc,
      endDateTime: endUtc,
      scheduleType: programData.scheduleType,
      recurrence: programData.recurrence || null,
      description: programData.description || null,
      thumbnail: programData.thumbnail || null,
    };

    try {
      if (programData.scheduleType === 'recurring' && programData.recurrence) {
        const result = await createRecurringProgram({
          channelId: numericChannelId,
          payload,
        }).unwrap();

        if (result.conflicts && result.conflicts.length > 0) {
          const conflictLines = result.conflicts.map((c: { startDateTime: string; endDateTime: string }) => {
            const start = new Date(c.startDateTime);
            return start.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
          }).join(', ');
          toast.warning(
            `${result.conflicts.length} occurrence(s) conflict with existing programs and were not scheduled: ${conflictLines}. Please choose different times for these slots.`,
            { duration: 10000 }
          );
        }
      } else {
        await createProgram({
          channelId: numericChannelId,
          payload,
        }).unwrap();
      }
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'Failed to save program.'));
    }
  };

  const handleUpdateProgram = async (
    programIdValue: string | number,
    programData: Omit<Program, 'id' | 'channelId'>,
    editMode?: 'single' | 'series'
  ) => {
    const numericProgramId = Number(programIdValue);
    if (!Number.isFinite(numericProgramId)) {
      return;
    }

    const startUtc = toUtcIso(new Date(programData.startDateTime));
    const endUtc = toUtcIso(new Date(programData.endDateTime));

    try {
      await updateProgram({
        programId: numericProgramId,
        payload: {
          title: programData.title,
          trackId: programData.trackId ? Number(programData.trackId) : null,
          startDateTime: startUtc,
          endDateTime: endUtc,
          scheduleType: programData.scheduleType,
          recurrence: programData.recurrence,
          description: programData.description || null,
          thumbnail: programData.thumbnail || null,
          editMode: editMode || 'single',
        },
      }).unwrap();
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'Failed to update program.'));
    }
  };

  const handleDeleteProgram = async (programIdValue: string | number) => {
    const numericProgramId = Number(programIdValue);
    if (!Number.isFinite(numericProgramId)) {
      return;
    }

    try {
      await deleteProgram(numericProgramId).unwrap();
    } catch (error) {
      throw new Error(getApiErrorMessage(error, 'Failed to delete program.'));
    }
  };

  const handleUploadTrack = async (file: File) => {
    if (!selectedChannel) {
      return;
    }

    const numericChannelId = Number(selectedChannel.id);
    if (!Number.isFinite(numericChannelId)) {
      return;
    }

    try {
      await uploadTrack({ channelId: numericChannelId, file }).unwrap();
    } catch (error) {
      const message = getApiErrorMessage(
        error,
        'Upload failed. File must be 48 kHz and ~128 kbps MP3.',
      );
      throw new Error(message);
    }
  };

  const handleDeleteTrack = async (trackIdValue: string | number) => {
    const numericTrackId = Number(trackIdValue);
    if (!Number.isFinite(numericTrackId)) {
      return;
    }

    await deleteTrack(numericTrackId).unwrap();
  };

  if (authRestoring) {
    return null;
  }

  return (
    <>
      {currentScreen === 'login' && <LoginScreen onLogin={handleLogin} />}

      {currentScreen === 'stations' && (
        <StationListScreen
          currentUsername={currentUsername}
          onSelectStation={handleSelectStation}
          onUpdateStation={handleUpdateStation}
          onDeleteStation={handleDeleteStation}
          onCreateStation={handleCreateStation}
          onLogout={handleLogout}
        />
      )}

      {currentScreen === 'channels' && selectedStation && (
        <ChannelListScreen
          station={selectedStation}
          userRole={userRole}
          currentUsername={currentUsername}
          onSelectChannel={handleSelectChannel}
          onCreateChannel={handleCreateChannel}
          onUpdateChannel={handleUpdateChannel}
          onDeleteChannel={handleDeleteChannel}
          onLogout={handleLogout}
          onBackToStations={userRole === 'superadmin' ? handleBackToStations : undefined}
        />
      )}

      {currentScreen === 'workspace' && selectedChannel && (
        <ChannelWorkspaceScreen
          channel={selectedChannel}
          stationName={selectedStation?.name ?? ''}
          currentUsername={currentUsername}
          onBack={handleBackToChannels}
          onLogout={handleLogout}
          onSaveProgram={handleSaveProgram}
          onUpdateProgram={handleUpdateProgram}
          onDeleteProgram={handleDeleteProgram}
          onUploadTrack={handleUploadTrack}
          onDeleteTrack={handleDeleteTrack}
        />
      )}

      <Toaster richColors position="top-right" />
    </>
  );
}